"use client";
import React, { useState } from "react";
import { ReviewType } from "../../../constants/mockPosts";
import { formatDate } from "src/app/lib/formatDate";
import { formatDateRelative } from "src/app/lib/formatDate";

const Review: React.FC<ReviewType> = ({
  photo,
  name,
  date,
  text,
  likes,
  dislikes,
  replies,
  variant,
}) => {
  const [showReplies, setShowReplies] = useState(false);

  return (
    <div className="flex flex-col text-primary gap-4 p-4  rounded-md shadow-sm">
      {/* Main Comment Section */}
      <div className="flex gap-4">
        {/* User Avatar */}
        <img
          className="w-12 h-12 rounded-full object-cover"
          src={photo}
          alt="user"
        />

        {/* Comment Content */}
        <div className="flex flex-col w-full">
          {/* Name */}
          <h3 className="h5 text-gray-800">{name}</h3>

          {/* Comment Text */}
          <p className="text-gray-700 text-pretty max-w-full mt-1">{text}</p>

          {/* Actions: Likes, Dislikes, Replies */}
          <div className="flex items-center xs:gap-2  gap-6 text-secondary-main text-sm mt-2">
            {replies.length > 0 && (
              <button
                className="text-themeColor whitespace-nowrap  flex gap-2 xs:gap-1 items-center hover:underline"
                onClick={() => setShowReplies(!showReplies)}
              >
                {showReplies ? "Hide Replies" : `View all replies`}{" "}
                <span
                  className={`transition-all duration-500 ${
                    showReplies ? "rotate-180 transform" : "transform rotate-0"
                  }`}
                >
                  <ArrowDown />
                </span>
              </button>
            )}
            <span className="flex items-center gap-1">
              <span className="size-[1.5rem]">
                <LikeIcon />
              </span>
              {likes}
            </span>
            <span className="flex items-center gap-1">
              <span className="size-[1.5rem]">
                <DislikeIcon />
              </span>
              {dislikes}
            </span>

            <span className="text-gray-400 whitespace-nowrap">
              {variant ? formatDateRelative(date) : formatDate(date)}
            </span>
          </div>
        </div>

        {replies.length && replies.length > 0 ? (
          <div className="text-themeColor sm:hidden flex gap-1">
            {replies.length}Replies
          </div>
        ) : (
          ""
        )}
      </div>

      {/* Conditionally Rendered Replies */}
      {showReplies && replies.length > 0 && (
        <div className="ml-10 mt-3 pl-4">
          {replies.map((reply, index) => (
            <Review key={index} {...reply} />
          ))}
        </div>
      )}
    </div>
  );
};

// ✅ Fixed ReviewParent Component
interface ReviewParentProps {
  reviews: ReviewType[];
}

interface ReviewParentProps {
  reviews: ReviewType[];
  variant?: boolean;
}

export const ReviewParent: React.FC<ReviewParentProps> = ({
  reviews,
  variant,
}) => {
  const [filter, setFilter] = useState<"positive" | "negative" | "neutral">(
    "positive"
  );
  const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
  // Filter reviews based on the selected type
  const filteredReviews = reviews?.filter((review) => review.type === filter);

  return (
    <div
      className={`space-y-4 max-w-full overflow-hidden mt-8 ${
        variant && "bg-white shadow-md p-4 py-6 rounded-xl"
      }`}
    >
      {!variant && <h2 className="">Reviews</h2>}

      {/* Filter Buttons */}
      <div
        className={`flex xs:gap-1  gap-4 ${variant && "w-max mx-auto"} mb-4`}
      >
        {Object.entries(buttons).map(([key, { icon, type, color }]) => (
          <button
            key={key}
            onClick={() =>
              setFilter(type as "positive" | "negative" | "neutral")
            }
            className="p-2 flex xs:gap-2 xs:p-1 gap-3 text-lg items-center rounded-lg transition-all"
            style={{
              border:
                filter === type
                  ? `2px solid ${color}`
                  : "2px solid transparent",
              color,
            }}
          >
            <span className="capitalize">{key}</span>
            <span className="size-[2rem] xs:size-[1.2rem]">{icon}</span>
            <span
              style={{
                color: color,
              }}
            >
              {
                reviews?.filter((item) => {
                  return item.type === type;
                }).length
              }
            </span>
          </button>
        ))}
      </div>

      {!isDemo && !reviews ? (
        <div>{`We're working hard to gather feedback from our valued clients.
At the moment, there are no reviews to display - but stay tuned, great testimonials are on the way!`}</div>
      ) : (
        <>
          {filteredReviews?.length > 0 ? (
            filteredReviews.map((review, index) => (
              <Review variant={variant} key={index} {...review} />
            ))
          ) : (
            <p className="text-gray-500 text-center">
              No {filter} reviews available.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewParent;

export const LikeIcon = () => {
  return (
    <svg
      width="100%"
      height="auto"
      viewBox="0 0 27 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.671 3.78859L13.671 3.7886L13.6743 3.7836C13.7911 3.60712 14.0356 3.40807 14.3639 3.29202C14.6853 3.17842 15.0151 3.16966 15.2905 3.27257L15.3028 3.27713L15.3151 3.2813C15.993 3.50957 16.4321 4.29531 16.2985 4.91884L16.294 4.93964L16.2907 4.96064L15.7385 8.4331L15.7369 8.44299L15.7356 8.45292C15.6649 8.98325 15.8075 9.49043 16.129 9.88759L16.1414 9.90287L16.1545 9.91753C16.4794 10.2806 16.9575 10.5121 17.4807 10.5121H21.8452C22.4693 10.5121 22.9294 10.7599 23.1813 11.1108C23.4116 11.4397 23.4863 11.9049 23.2921 12.4564L23.2893 12.4643L23.2867 12.4724L20.6744 20.4261L20.6654 20.4535L20.6584 20.4815C20.4242 21.4182 19.3298 22.2675 18.2453 22.2675H14.1038C13.8289 22.2675 13.4782 22.2187 13.153 22.1216C12.8115 22.0196 12.5937 21.8929 12.5007 21.7999L12.4649 21.7641L12.4248 21.7331L11.0656 20.6818L11.0656 20.6817L11.0582 20.6762C10.7409 20.4366 10.5464 20.0472 10.5464 19.6339V8.84489C10.5464 8.57973 10.626 8.31946 10.7729 8.09853C10.7732 8.09813 10.7735 8.09773 10.7737 8.09733L13.671 3.78859Z"
        stroke="#A4A7B0"
        stroke-width="1.59287"
      />
      <path
        d="M5.27982 7.57109H6.37359C7.12293 7.57109 7.4472 7.72159 7.59864 7.86603C7.74075 8.00157 7.89213 8.28749 7.89213 8.98344V19.6663C7.89213 20.3622 7.74075 20.6482 7.59864 20.7837C7.4472 20.9281 7.12293 21.0786 6.37359 21.0786H5.27982C4.53047 21.0786 4.2062 20.9281 4.05477 20.7837C3.91265 20.6482 3.76128 20.3622 3.76128 19.6663V8.98344C3.76128 8.28749 3.91265 8.00157 4.05477 7.86603C4.2062 7.72159 4.53047 7.57109 5.27982 7.57109Z"
        stroke="#A4A7B0"
        stroke-width="1.59287"
      />
    </svg>
  );
};

export const DislikeIcon = () => {
  return (
    <svg
      width="100%"
      height="auto"
      viewBox="0 0 26 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.7626 21.6958L12.7626 21.6958L12.7593 21.7008C12.6425 21.8773 12.398 22.0763 12.0696 22.1924C11.7483 22.306 11.4185 22.3147 11.1431 22.2118L11.1308 22.2072L11.1185 22.2031C10.4406 21.9748 10.0015 21.1891 10.1351 20.5655L10.1396 20.5447L10.1429 20.5237L10.6951 17.0513L10.6967 17.0414L10.698 17.0315C10.7687 16.5011 10.6261 15.9939 10.3046 15.5968L10.2922 15.5815L10.2791 15.5668C9.95422 15.2037 9.47605 14.9723 8.95286 14.9723H4.58839C3.96428 14.9723 3.50422 14.7245 3.25234 14.3736C3.02203 14.0447 2.94728 13.5795 3.14145 13.028L3.14426 13.02L3.1469 13.012L5.75921 5.05828L5.7682 5.03089L5.7752 5.00292C6.00937 4.06621 7.1038 3.21689 8.18828 3.21689H12.3297C12.6047 3.21689 12.9554 3.26568 13.2806 3.36278C13.6221 3.46478 13.8399 3.59151 13.9329 3.68448L13.9687 3.72031L14.0088 3.7513L15.368 4.8026L15.368 4.80264L15.3754 4.8082C15.6927 5.0478 15.8872 5.43715 15.8872 5.85043V16.6395C15.8872 16.9046 15.8076 17.1649 15.6607 17.3858C15.6604 17.3862 15.6601 17.3866 15.6599 17.387L12.7626 21.6958Z"
        stroke="#A4A7B0"
        stroke-width="1.59287"
      />
      <path
        d="M21.1538 17.9133H20.06C19.3107 17.9133 18.9864 17.7628 18.835 17.6183C18.6928 17.4828 18.5415 17.1969 18.5415 16.5009V5.81808C18.5415 5.12213 18.6928 4.8362 18.835 4.70067C18.9864 4.55623 19.3107 4.40573 20.06 4.40573H21.1538C21.9031 4.40573 22.2274 4.55623 22.3788 4.70067C22.5209 4.8362 22.6723 5.12213 22.6723 5.81808V16.5009C22.6723 17.1969 22.5209 17.4828 22.3788 17.6183C22.2274 17.7628 21.9031 17.9133 21.1538 17.9133Z"
        stroke="#A4A7B0"
        stroke-width="1.59287"
      />
    </svg>
  );
};

const FillLike = () => {
  return (
    <svg
      width="100%"
      height="auto"
      viewBox="0 0 25 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.3027 2.1062C16.5723 2.36011 17.3975 3.59546 17.1436 4.86499L17.0312 5.42163C16.7725 6.72534 16.2939 7.96558 15.625 9.09351H22.6562C23.9502 9.09351 25 10.1433 25 11.4373C25 12.3406 24.4873 13.1267 23.7354 13.5173C24.2676 13.947 24.6094 14.6062 24.6094 15.3435C24.6094 16.4861 23.7891 17.4382 22.71 17.6433C22.9248 17.9998 23.0469 18.4148 23.0469 18.8591C23.0469 19.8992 22.3682 20.783 21.4307 21.0857C21.4648 21.2468 21.4844 21.4177 21.4844 21.5935C21.4844 22.8875 20.4346 23.9373 19.1406 23.9373H14.3799C13.4521 23.9373 12.5488 23.6638 11.7773 23.1511L9.89746 21.8962C8.59375 21.0271 7.8125 19.5623 7.8125 17.9949V12.5652C7.8125 11.1394 8.46191 9.79663 9.57031 8.90308L9.93164 8.61499C11.2256 7.57983 12.1094 6.12476 12.4316 4.50366L12.5439 3.94702C12.7979 2.67749 14.0332 1.85229 15.3027 2.1062ZM1.5625 9.87476H4.6875C5.55176 9.87476 6.25 10.573 6.25 11.4373V22.3748C6.25 23.239 5.55176 23.9373 4.6875 23.9373H1.5625C0.698242 23.9373 0 23.239 0 22.3748V11.4373C0 10.573 0.698242 9.87476 1.5625 9.87476Z"
        fill="#01BA4C"
      />
    </svg>
  );
};

const FillNeutral = () => {
  return (
    <svg
      width="100%"
      height="auto"
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1715_11397)">
        <path
          d="M17.5451 25.7791L25.2743 18.0636L28.1694 20.9586L20.4402 28.6741L17.5451 25.7791ZM21.915 14.6769L17.6817 10.4709C17.5201 10.3127 17.3361 10.1793 17.1355 10.0749L15.606 4.21656C15.5079 3.87562 15.2811 3.58617 14.9735 3.40939C14.6659 3.23261 14.3016 3.18235 13.9576 3.26923C13.6136 3.35612 13.3169 3.57333 13.1302 3.87497C12.9434 4.17662 12.8812 4.53906 12.9568 4.8857L14.4043 10.4436L11.2225 3.8342C10.9084 3.15141 10.0891 2.89195 9.40627 3.17872C8.72348 3.50646 8.4367 4.32581 8.76444 5.0086L11.0723 9.84276L6.47027 5.28172C5.93769 4.74914 5.06372 4.74914 4.53114 5.28172C3.99856 5.82795 4.01222 6.68827 4.5448 7.22085L9.62476 12.2735L8.66885 12.6422L7.19402 13.2294L4.58576 15.032C4.58576 15.032 3.47964 16.4658 3.38405 17.0804C3.2748 17.6949 4.46286 20.7811 4.46286 20.7811H4.47652C4.69501 21.2454 5.15931 21.5731 5.70554 21.5731C6.06772 21.5731 6.41506 21.4292 6.67115 21.1731C6.92725 20.9171 7.07112 20.5697 7.07112 20.2075C7.07112 20.071 7.03016 19.9617 6.98919 19.8388L7.0165 19.8252L6.21081 17.4627L7.89047 16.1244C8.5323 16.1518 9.91153 16.261 10.5807 16.302C14.3087 19.6476 10.7855 20.9586 10.7855 20.9586L6.06059 22.4061L5.82844 22.5973C5.68833 22.7268 5.57885 22.886 5.50798 23.0631C5.43711 23.2403 5.40664 23.4311 5.41877 23.6215L5.44608 25.0827L14.2268 24.3999C14.7457 24.4135 15.2373 24.2223 15.6197 23.8536L21.915 17.572C22.6388 16.8345 22.6661 15.4553 21.915 14.6769Z"
          fill="#FFBB53"
        />
      </g>
      <defs>
        <clipPath id="clip0_1715_11397">
          <rect width="28" height="28" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const FillNegative = () => {
  return (
    <svg
      width="100%"
      height="auto"
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.1391 26.2008C18.5609 25.9164 19.4852 24.5328 19.2008 23.1109L19.075 22.4875C18.7852 21.0273 18.2492 19.6383 17.5 18.375H25.375C26.8242 18.375 28 17.1992 28 15.75C28 14.7383 27.4258 13.8578 26.5836 13.4203C27.1797 12.9391 27.5625 12.2008 27.5625 11.375C27.5625 10.0953 26.6438 9.02891 25.4352 8.79922C25.6758 8.4 25.8125 7.93516 25.8125 7.4375C25.8125 6.27266 25.0523 5.28281 24.0023 4.94375C24.0406 4.76328 24.0625 4.57188 24.0625 4.375C24.0625 2.92578 22.8867 1.75 21.4375 1.75H16.1055C15.0664 1.75 14.0547 2.05625 13.1906 2.63047L11.0852 4.03594C9.625 5.00938 8.75 6.65 8.75 8.40547V14.4867C8.75 16.0836 9.47734 17.5875 10.7188 18.5883L11.1234 18.9109C12.5727 20.0703 13.5625 21.7 13.9234 23.5156L14.0492 24.1391C14.3336 25.5609 15.7172 26.4852 17.1391 26.2008ZM1.75 21H5.25C6.21797 21 7 20.218 7 19.25V7C7 6.03203 6.21797 5.25 5.25 5.25H1.75C0.782031 5.25 0 6.03203 0 7V19.25C0 20.218 0.782031 21 1.75 21Z"
        fill="#E9212E"
      />
    </svg>
  );
};

const buttons = {
  positive: {
    icon: <FillLike />,
    type: "positive",
    color: "#01BA4C", // Green border
  },
  neutral: {
    type: "neutral",
    icon: <FillNeutral />,
    color: "#FFBB53", // Orange border
  },
  negative: {
    type: "negative",
    icon: <FillNegative />,
    color: "#FF3B30", // Red border
  },
};

const ArrowDown = () => {
  return (
    <svg
      width="25"
      height="25"
      viewBox="0 0 25 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.1163 9.46164L12.1948 15.5392L6.11729 9.61769"
        stroke="var(--primary-color)"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};
